import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import BibleStudyForm, { BibleStudy } from '../components/BibleStudyForm';
import AttendanceModal from '../components/AttendanceModal';
import TableFilter from '../components/TableFilter';
import ActionButtons from '../components/ActionButtons';
import DetailsPanel from '../components/DetailsPanel';
import ReportModal from '../components/ReportModal';

const BibleStudiesPage: React.FC = () => {
  const [studies, setStudies] = useState<BibleStudy[]>([]);
  const [filteredStudies, setFilteredStudies] = useState<BibleStudy[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingStudy, setEditingStudy] = useState<BibleStudy | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrLink, setQrLink] = useState('');
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedStudyForAttendance, setSelectedStudyForAttendance] = useState<BibleStudy | null>(null);

  // Modal de Relatórios
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; rowId?: number; isRowReport: boolean }>({
    isOpen: false,
    isRowReport: false
  });

  const [selectedStudy, setSelectedStudy] = useState<BibleStudy | null>(null);

  const fetchStudies = useCallback(async () => {
    try {
      const response = await api.get('/bible-studies/');
      setStudies(response.data);
      setFilteredStudies(response.data);
    } catch (error) {
      console.error("Error fetching bible studies", error);
    }
  }, []);

  useEffect(() => {
    fetchStudies();
  }, [fetchStudies]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = studies;

    if (filters.topic) {
      result = result.filter(s => s.topic.toLowerCase().includes(filters.topic.toLowerCase()));
    }
    if (filters.group) {
      result = result.filter(s => s.group_type === filters.group);
    }
    if (filters.date) {
      result = result.filter(s => s.date.startsWith(filters.date));
    }

    setFilteredStudies(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingStudy(null);
    fetchStudies();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingStudy(null);
  };

  const handleEdit = (study: BibleStudy) => {
    setEditingStudy(study);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este estudo bíblico?')) {
      try {
        await api.delete(`/bible-studies/${id}//`);
        fetchStudies();
        if (selectedStudy?.id === id) setSelectedStudy(null);
      } catch (error) {
        console.error("Error deleting bible study", error);
        alert('Erro ao excluir estudo bíblico.');
      }
    }
  };

  const handleShowQR = (study: BibleStudy) => {
    const link = `${window.location.origin}/checkin/biblestudy/${study.id}`;
    setQrLink(link);
    setShowQRModal(true);
  };

  const handleOpenAttendance = (study: BibleStudy) => {
    setSelectedStudyForAttendance(study);
    setShowAttendanceModal(true);
  };

  const handlePrintQR = () => {
    const printWindow = window.open('', '', 'width=600,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Code - Check-in</title>
            <style>
              body { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; }
              h1 { margin-bottom: 20px; }
              img { width: 400px; height: 400px; }
              p { margin-top: 20px; font-size: 1.2rem; }
            </style>
          </head>
          <body>
            <h1>Check-in de Presença</h1>
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qrLink)}" />
            <p>${qrLink}</p>
            <script>
              window.onload = function() { window.print(); window.close(); }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pending': return <span className="badge badge-warning">Pendente</span>;
      case 'Completed': return <span className="badge badge-success">Concluído</span>;
      case 'Canceled': return <span className="badge badge-danger">Cancelado</span>;
      case 'Postponed': return <span className="badge badge-info">Adiado</span>;
      default: return <span className="badge badge-secondary">{status}</span>;
    }
  };

  const openGeneralReports = () => setReportModal({ isOpen: true, isRowReport: false });
  const openRowReport = (id: number) => setReportModal({ isOpen: true, rowId: id, isRowReport: true });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Estudos Bíblicos</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Acompanhe os encontros semanais</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openGeneralReports}>Relatórios de Estudos</button>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>
              + Novo Encontro
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <BibleStudyForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            studyToEdit={editingStudy}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'topic', label: 'Tema' },
            {
              key: 'group', label: 'Grupo', type: 'select', options: [
                { value: 'Men', label: 'Homens' },
                { value: 'Women', label: 'Mulheres' }
              ]
            },
            { key: 'date', label: 'Data', type: 'date' }
          ]}
          onFilter={handleFilter}
        />
      )}

      {showQRModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '400px', maxWidth: '90%', textAlign: 'center', padding: '40px' }}>
            <h3 style={{ marginBottom: '20px' }}>Check-in de Presença</h3>
            <div style={{ background: 'white', padding: '20px', display: 'inline-block', borderRadius: '8px' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrLink)}`}
                alt="QR Code"
                style={{ width: '250px', height: '250px' }}
              />
            </div>
            <p style={{ marginTop: '20px', wordBreak: 'break-all', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              {qrLink}
            </p>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button className="btn" onClick={handlePrintQR}>Imprimir</button>
              <button className="btn btn-secondary" onClick={() => setShowQRModal(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {showAttendanceModal && selectedStudyForAttendance && selectedStudyForAttendance.id && (
        <AttendanceModal
          type="bible-studies"
          itemId={selectedStudyForAttendance.id}
          title={selectedStudyForAttendance.topic}
          onClose={() => setShowAttendanceModal(false)}
        />
      )}

      {reportModal.isOpen && (
        <ReportModal 
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ ...reportModal, isOpen: false })}
          category="Bible Study"
          rowId={reportModal.rowId}
          isRowReport={reportModal.isRowReport}
        />
      )}

      <div className="page-container">
        <div className="list-container card" style={{ overflowX: 'auto', padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Grupo</th>
                <th>Tema</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudies.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                    Nenhum estudo encontrado.
                  </td>
                </tr>
              ) : (
                filteredStudies.map((study) => (
                  <tr
                    key={study.id}
                    onClick={() => setSelectedStudy(study)}
                    className={selectedStudy?.id === study.id ? 'selected' : ''}
                  >
                    <td>{new Date(study.date).toLocaleDateString()}</td>
                    <td>
                      <span className={'badge ' + (study.group_type === 'Men' ? 'badge-info' : 'badge-warning')}>
                        {study.group_type === 'Men' ? 'Homens' : 'Mulheres'}
                      </span>
                    </td>
                    <td style={{ fontWeight: '500' }}>{study.topic}</td>
                    <td>{getStatusBadge(study.status || 'Pending')}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <ActionButtons
                        onQrCode={() => handleShowQR(study)}
                        onAttendance={() => handleOpenAttendance(study)}
                        onReport={() => study.id && openRowReport(study.id)}
                        onEdit={() => handleEdit(study)}
                        onDelete={() => study.id && handleDelete(study.id)}
                        deletePermission="delete_biblestudy"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedStudy && (
          <DetailsPanel title="Detalhes do Estudo" onClose={() => setSelectedStudy(null)}>
            <div className="details-row">
              <div className="details-label">Tema</div>
              <div className="details-value">{selectedStudy.topic}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Data e Hora</div>
              <div className="details-value">
                {new Date(selectedStudy.date).toLocaleString()}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Grupo</div>
              <div className="details-value">
                {selectedStudy.group_type === 'Men' ? 'Homens' : 'Mulheres'}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Responsável</div>
              <div className="details-value">{selectedStudy.responsible_name || '-'}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Status</div>
              <div className="details-value">{getStatusBadge(selectedStudy.status || 'Pending')}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Participação</div>
              <div className="details-value">
                <span style={{ fontWeight: 'bold', color: (selectedStudy.actual_attendees_count || 0) >= (selectedStudy.expected_attendees || 0) ? 'var(--success)' : 'var(--text-main)' }}>
                  {selectedStudy.actual_attendees_count || 0}
                </span>
                <span style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}> / {selectedStudy.expected_attendees || 0} previstos</span>
              </div>
            </div>
          </DetailsPanel>
        )}
      </div>
    </div>
  );
};

export default BibleStudiesPage;
